import { useCallback, useEffect, useRef, useState } from 'react';
import { inventarioApi, isOfflineError, cache } from '../services/inventarioApi';
import { offlineQueue } from '../services/offlineQueue';
import {
  combinarFechaConHoraActual,
  toLocalISOString,
  formatearFechaLocal,
  normalizarFechaISO,
} from '../../../utils/horaLocal';
import {v4 as uuid} from 'uuid';

const ENTIDAD = 'ajustes';

export function useAjustes(arqueosExternos = []) {
  const [ajustes, setAjustes] = useState([]);
  const [loading, setLoading] = useState(true);
  const repairDone = useRef(false);

  const fetchAjustes = useCallback(async () => {
    try {
      const data = await inventarioApi.getAll(ENTIDAD);
      setAjustes(data);
      cache.set(ENTIDAD, data);
    } catch (e) {
      if (isOfflineError(e)) setAjustes(cache.get(ENTIDAD) || []);
      else console.error('Error fetch ajustes', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAjustes(); }, [fetchAjustes]);

  // Auto-repair: ajustes whose parent arqueo was annulled but they were never patched
  const repararAjustesHuerfanos = useCallback(async (ajustesActuales = ajustes) => {
    const huerfanos = ajustesActuales.filter(a => {
      if (a.anulado) return false;
      if (!a.arqueoDiaId) return false;
      const arqueo = arqueosExternos.find(arq => arq.id === a.arqueoDiaId);
      return arqueo && arqueo.anulado;
    });
    if (huerfanos.length === 0) return [];
    console.warn(`[ajustes] Reparando ${huerfanos.length} ajuste(s) huérfano(s):`, huerfanos.map(a => a.id));
    for (const a of huerfanos) {
      try {
        await inventarioApi.patch(ENTIDAD, { id: a.id, anulado: true });
      } catch (e) {
        if (isOfflineError(e)) {
          offlineQueue.enqueue({ method: 'PATCH', entidad: ENTIDAD, body: { id: a.id, anulado: true } });
        }
      }
    }
    const huerfanoIds = new Set(huerfanos.map(a => a.id));
    setAjustes(prev => {
      const updated = prev.map(a => huerfanoIds.has(a.id) ? { ...a, anulado: true } : a);
      cache.set(ENTIDAD, updated);
      return updated;
    });
    return huerfanos;
  }, [ajustes, arqueosExternos]);

  useEffect(() => {
    if (loading || repairDone.current || arqueosExternos.length === 0) return;
    repairDone.current = true;
    repararAjustesHuerfanos(ajustes);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, arqueosExternos]);

  const esAjusteAnulado = (ajuste) => {
    if (ajuste.anulado) return true;
    if (ajuste.arqueoDiaId) {
      const arqueo = arqueosExternos.find(a => a.id === ajuste.arqueoDiaId);
      return arqueo && arqueo.anulado;
    }
    return false;
  };

  const guardarAjuste = async (nuevoAjuste) => {
    const { id, ...body } = nuevoAjuste;
    try {
      const created = await inventarioApi.create(ENTIDAD, {id, ...body});
      setAjustes(prev => {
        const updated = [...prev, created];
        cache.set(ENTIDAD, updated);
        return updated;
      });
      return created;
    } catch (e) {
      if (isOfflineError(e)) {
        const temp = { id: id || `temp_${Date.now()}`, ...body };
        setAjustes(prev => {
          const updated = [...prev, temp];
          cache.set(ENTIDAD, updated);
          return updated;
        });
        offlineQueue.enqueue({ method: 'POST', entidad: ENTIDAD, body });
        return temp;
      }
      return false;
    }
  };

  const registrarAjusteManual = async (tipo, monto, descripcion = '', metodoPago = 'EFECTIVO', fechaRegistro = null) => {
    if (isNaN(monto) || monto <= 0) {
      alert('Monto inválido');
      return false;
    }
    const body = {
      id: uuid(),
      tipo,
      monto: parseFloat(monto),
      descripcion,
      fecha: combinarFechaConHoraActual(fechaRegistro),
      fuente: 'MANUAL',
      anulado: false,
      metodoPago,
    };
    try {
      const created = await inventarioApi.create(ENTIDAD, body);
      setAjustes(prev => {
        const updated = [...prev, created];
        cache.set(ENTIDAD, updated);
        return updated;
      });
      return true;
    } catch (e) {
      if (isOfflineError(e)) {
        const temp = { id: `temp_${Date.now()}`, ...body };
        setAjustes(prev => {
          const updated = [...prev, temp];
          cache.set(ENTIDAD, updated);
          return updated;
        });
        offlineQueue.enqueue({ method: 'POST', entidad: ENTIDAD, body });
        return true;
      }
      return false;
    }
  };

  const anularAjustesPorArqueo = async (arqueoDiaId) => {
    const afectados = ajustes.filter(a => a.arqueoDiaId === arqueoDiaId && !a.anulado);
    for (const a of afectados) {
      console.log('Anulando ajuste', a.id);
      try {
        await inventarioApi.patch(ENTIDAD, { id: a.id, anulado: true });
      } catch (e) {
        if (isOfflineError(e)) {
          offlineQueue.enqueue({ method: 'PATCH', entidad: ENTIDAD, body: { id: a.id, anulado: true } });
        }
      }
    }
    setAjustes(prev => {
      const updated = prev.map(a => a.arqueoDiaId === arqueoDiaId ? { ...a, anulado: true } : a);
      cache.set(ENTIDAD, updated);
      return updated;
    });
    return true;
  };

  const anularAjuste = async id => {
    if (!window.confirm('¿Anular este registro?')) return false;
    try { await inventarioApi.patch(ENTIDAD, { id, anulado: true }); await fetchAjustes(); return true; }
    catch (e) {       alert(e.response?.data?.message || 'Conéctate para validar y guardar la corrección');
      return false; }
  };
  const editarAjuste = async (id, monto) => {
    try { await inventarioApi.patch(ENTIDAD, { id, monto: Number(monto) }); await fetchAjustes(); return true; }
    catch (e) {       alert(e.response?.data?.message || 'Conéctate para validar y guardar la corrección');
      return false; }
  };
  const editarFechaAjuste = async (id, fecha) => {
    try { await inventarioApi.patch(ENTIDAD, { id, fecha }); await fetchAjustes(); return true; }
    catch (e) {       alert(e.response?.data?.message || 'Conéctate para validar y guardar la corrección');
      return false; }
  };

  const obtenerAjustes = () => ajustes.filter(a => !esAjusteAnulado(a));
  const obtenerAjustesInclusoAnulados = () => ajustes;

  const obtenerAjustesDiarios = () => {
    const hoy = formatearFechaLocal(normalizarFechaISO(toLocalISOString()));
    return ajustes.filter(a => {
      const fechaAjuste = formatearFechaLocal(normalizarFechaISO(a.fecha));
      return fechaAjuste === hoy && !esAjusteAnulado(a);
    });
  };

  const obtenerAjustesPorFecha = (fecha) => {
    const formatoFecha = formatearFechaLocal(normalizarFechaISO(fecha));
    return ajustes.filter(a => {
      const fechaAjuste = formatearFechaLocal(normalizarFechaISO(a.fecha));
      return fechaAjuste === formatoFecha && !esAjusteAnulado(a);
    });
  };

  return {
    ajustes,
    esAjusteAnulado,
    guardarAjuste,
    registrarAjusteManual,
    anularAjustesPorArqueo,
    anularAjuste,
    editarAjuste,
    editarFechaAjuste,
    obtenerAjustes,
    obtenerAjustesInclusoAnulados,
    obtenerAjustesDiarios,
    obtenerAjustesPorFecha,
    repararAjustesHuerfanos,
    loading,
    refetch: fetchAjustes,
  };
}